import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Wallet, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import axiosInstance from '../../config/axiosInstance';

const formatBalance = (value, currency) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 'N/A';
  const decimals = ['BTC', 'ETH'].includes(currency) ? 8 : 2;
  return parsed.toFixed(decimals);
};

const UpdateUsdtBalanceModal = ({ isOpen, onClose, user, onUpdated }) => {
  const [amount, setAmount] = useState('');
  const [operation, setOperation] = useState('ADD');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const uid = user?.uid || '';
  const currency = user?.wallet?.currencyCode || 'USDT';

  const canSubmit = useMemo(() => {
    const parsed = Number(amount);
    if (!uid) return false;
    if (!Number.isFinite(parsed) || parsed <= 0) return false;
    if (!['ADD', 'SUBTRACT'].includes(operation)) return false;
    if (!reason.trim()) return false;
    return true;
  }, [amount, operation, reason, uid]);

  useEffect(() => {
    if (!isOpen) return;
    setAmount('');
    setOperation('ADD');
    setReason('');
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canSubmit) {
      toast.error('Please fill all fields correctly');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        uid: String(uid),
        currency,
        amount: Number(amount),
        operation,
        reason: reason.trim(),
      };

      const response = await axiosInstance.put(
        '/api/admin/users/balance',
        payload,
      );

      if (response.data?.success) {
        toast.success(`${currency} balance updated successfully`);
        const dataForCallback = response.data?.data;
        onUpdated?.(dataForCallback);
        onClose?.();
        return;
      }

      toast.error(
        response.data?.message || `Failed to update ${currency} balance`,
      );
    } catch (error) {
      console.error('Error updating balance:', error);
      const apiMessage = error.response?.data?.message;
      const apiErrors = error.response?.data?.errors;
      const details =
        apiErrors && typeof apiErrors === 'object'
          ? Object.values(apiErrors).flat().filter(Boolean).join(', ')
          : null;
      toast.error(
        details || apiMessage || `Failed to update ${currency} balance`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className='modal d-block'
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div
        className='modal-dialog modal-dialog-centered modal-lg'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='modal-content border-0 shadow-lg'>
          <div
            className='modal-header text-white border-0'
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            <h5 className='modal-title d-flex align-items-center gap-2'>
              <Wallet size={22} /> Update {currency} Balance
            </h5>
            <button
              type='button'
              className='btn-close btn-close-white'
              onClick={onClose}
              aria-label='Close'
            ></button>
          </div>

          <div className='modal-body p-4'>
            <div className='card bg-light border-0 mb-4'>
              <div className='card-body'>
                <h6 className='card-title fw-bold mb-3'>User</h6>
                <div className='row g-3'>
                  <div className='col-md-6'>
                    <small className='text-muted d-block'>UID</small>
                    <strong>{user?.uid || 'N/A'}</strong>
                  </div>
                  <div className='col-md-6'>
                    <small className='text-muted d-block'>Email</small>
                    <strong>{user?.email || 'N/A'}</strong>
                  </div>
                  <div className='col-md-6'>
                    <small className='text-muted d-block'>Currency</small>
                    <strong>{currency}</strong>
                  </div>
                  <div className='col-md-6'>
                    <small className='text-muted d-block'>
                      Available Balance
                    </small>
                    <strong>
                      {formatBalance(user?.wallet?.availableBalance, currency)}
                    </strong>
                  </div>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className='row g-3'>
                <div className='col-md-6'>
                  <label className='form-label fw-semibold'>UID</label>
                  <input className='form-control' value={uid} disabled />
                </div>

                <div className='col-md-6'>
                  <label className='form-label fw-semibold'>Amount</label>
                  <input
                    type='number'
                    className='form-control'
                    value={amount}
                    min='0'
                    step='any'
                    inputMode='decimal'
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder='e.g. 10'
                    disabled={submitting}
                  />
                  <small className='text-muted'>Must be greater than 0</small>
                </div>

                <div className='col-md-6'>
                  <label className='form-label fw-semibold'>Operation</label>
                  <select
                    className='form-select'
                    value={operation}
                    onChange={(e) => setOperation(e.target.value)}
                    disabled={submitting}
                  >
                    <option value='ADD'>Increment</option>
                    <option value='SUBTRACT'>Decrement</option>
                  </select>
                </div>

                <div className='col-md-6'>
                  <label className='form-label fw-semibold'>Reason</label>
                  <input
                    type='text'
                    className='form-control'
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder='e.g. Compensation for system error'
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className='d-flex justify-content-end gap-2 mt-4'>
                <button
                  type='button'
                  className='btn btn-outline-secondary'
                  onClick={onClose}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  className='btn btn-primary d-flex align-items-center gap-2'
                  disabled={!canSubmit || submitting}
                >
                  {operation === 'ADD' ? (
                    <ArrowUpCircle size={18} />
                  ) : (
                    <ArrowDownCircle size={18} />
                  )}
                  {submitting ? 'Updating...' : 'Update'}
                </button>
              </div>

              <div className='alert alert-info small mt-4 mb-0'>
                <strong>Note:</strong> Operation can be{' '}
                <strong>Increment</strong> or <strong>Decrement</strong>.
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateUsdtBalanceModal;
